import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	buildFilterPayload,
	isFilterActive,
	selectedRange,
} from '@components/propertyFilters/filterUtils';
import { useToast } from '@contexts/Toast.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import { updateCollection } from '@utils/api/collections';
import { COLLECTION_CONFIG_SCOPE, invalidateCollectionConfig } from '@utils/collectionQueries';

// ─── Helper functions (pure, no hooks) ──────────────────────────────────────────

/**
 * Adapts the collection data into the internal filter config shape.
 * The new data structure already has filters in the correct shape from buildCollectionFilters.
 */
const normalizeFilterConfigs = collectionData => {
	const { collectionFilters } = collectionData ?? {};
	if (!collectionFilters?.length) return [];
	return collectionFilters.map(filter => ({
		id: filter.id,
		label: filter.label,
		unit: filter.unit,
		conversionFactor: filter.conversionFactor,
		min: filter.min,
		max: filter.max,
		selectedMin: filter.selectedMin ?? null,
		selectedMax: filter.selectedMax ?? null,
	}));
};

/**
 * Builds Form initialValues from configs.
 * Uses saved selection when present, otherwise the full range.
 */
const buildFormInitialValues = configs => {
	const values = {};
	for (const c of configs) {
		values[c.id] = selectedRange(c);
	}
	return values;
};

/** Compares two { min, max } objects by value. */
const rangesDiffer = (a, b) => {
	if (!a || !b) return a !== b;
	return a.min !== b.min || a.max !== b.max;
};

// ─── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Connects collection data to an antd Form for filter state management.
 *
 * Architecture:
 * - The antd Form store is the canonical draft state.
 * - `currentValues` (useState) is a React-visible mirror so derived state
 *   (isDirty, activeFilters, previewCount) triggers re-renders.
 * - `initialValuesRef` (useRef) holds the saved baseline — used inside callbacks
 *   without causing them to be recreated on every baseline change.
 *
 * @param {object} collectionData - Raw API response (from React Query)
 * @param {string} projectUuid
 * @param {string} collectionUuid
 */
const useCollectionFilters = (collectionData, projectUuid, collectionUuid) => {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	const [form] = Form.useForm();

	// ─── 1. Derived config & baseline ─────────────────────────────────────────

	const filterConfigs = useMemo(() => normalizeFilterConfigs(collectionData), [collectionData]);

	// The "saved baseline" — what's currently persisted on the backend.
	const initialValues = useMemo(() => buildFormInitialValues(filterConfigs), [filterConfigs]);

	// Ref: lets callbacks compare against the current baseline without
	// needing the baseline in their dependency array (avoids recreating them).
	const initialValuesRef = useRef(initialValues);

	// ─── 2. Draft state (mirror of Form store for React reactivity) ───────────

	const [currentValues, setCurrentValues] = useState(initialValues);
	const [isDirty, setIsDirty] = useState(false);
	const [dirtyFilters, setDirtyFilters] = useState({});

	// ─── 3. Form re-seeding on external data change ───────────────────────────
	//
	// After a successful Apply (or another panel's Apply), React Query refetches
	// and collectionData changes. The fingerprint detects meaningful config changes
	// (new bounds or saved selections) and re-seeds the form.
	//
	// The ref is initialized to the current fingerprint so the effect is a no-op
	// on first mount — the Form element receives initialValues at construction.

	const configFingerprint = useMemo(
		() =>
			filterConfigs
				.map(c => `${c.id}:${c.min}:${c.max}:${c.selectedMin}:${c.selectedMax}`)
				.join('|'),
		[filterConfigs]
	);
	const configFingerprintRef = useRef(configFingerprint);

	useEffect(() => {
		if (configFingerprint === configFingerprintRef.current) return;
		configFingerprintRef.current = configFingerprint;
		initialValuesRef.current = initialValues;
		form.resetFields();
		form.setFieldsValue(initialValues);
		setCurrentValues(initialValues);
		setDirtyFilters({});
		setIsDirty(false);
	}, [configFingerprint, initialValues, form]);

	// ─── 4. Change tracking (Form → React state) ─────────────────────────────
	//
	// Wired to the Form's onValuesChange. Mirrors values into React state and
	// computes per-field dirty flags by comparing against the saved baseline.
	//
	// Empty deps: safe because it reads the baseline via ref, not closure.

	const handleValuesChange = useCallback((_, allValues) => {
		setCurrentValues(allValues);

		const saved = initialValuesRef.current;
		let anyDirty = false;
		const dirty = {};
		for (const id of Object.keys(saved)) {
			const fieldDirty = rangesDiffer(allValues[id], saved[id]);
			dirty[id] = fieldDirty;
			if (fieldDirty) anyDirty = true;
		}
		setDirtyFilters(dirty);
		setIsDirty(anyDirty);
	}, []);

	// ─── 5. Derived display state ────────────────────────────────────────────

	// "Active" = value restricts the range (vs. full range), regardless of saved/draft.
	const activeFilters = useMemo(() => {
		const active = {};
		for (const config of filterConfigs) {
			active[config.id] = isFilterActive(currentValues[config.id], config);
		}
		return active;
	}, [currentValues, filterConfigs]);

	const activeCount = useMemo(
		() => Object.values(activeFilters).filter(Boolean).length,
		[activeFilters]
	);

	// Sync to Zustand so CollectionHeader can show a badge without prop-drilling.
	const setActiveFilterCount = useCollectionStore(s => s.setActiveFilterCount);

	useEffect(() => {
		setActiveFilterCount(activeCount);
	}, [activeCount, setActiveFilterCount]);

	// Shown count — subscribed via a primitive selector so the hook only
	// re-renders when the total count changes, not on every store update.
	const shownCount = useCollectionStore(s => s.filteredFeatures.length);
	const totalCount = collectionData?.totalCount ?? 0;

	// ─── 6. Persistence (mutation) ───────────────────────────────────────────

	const saveMutation = useMutation({
		// Same scope as every other collection-config write — these must not overlap.
		scope: COLLECTION_CONFIG_SCOPE(collectionUuid),
		mutationFn: filters =>
			updateCollection(collectionUuid, {
				filters,
			}),
		onSuccess: () => {
			// A filter change doesn't change what the model found.
			invalidateCollectionConfig(queryClient, collectionUuid);
			addToast({
				id: 'collection-filters-applied',
				title: 'Filters applied',
				bg: 'success',
				delay: 2500,
			});
		},
		onError: () => {
			addToast({
				id: 'collection-filters-error',
				title: 'Failed to save filters',
				bg: 'danger',
				delay: 4000,
			});
		},
	});
	// `mutate` is referentially stable; the mutation result object is not (safe as a hook dep)
	const { mutate: saveMutate } = saveMutation;

	// ─── 7. User actions ─────────────────────────────────────────────────────

	/** Persist current draft to backend; advance baseline on success. */
	const apply = useCallback(() => {
		const snapshot = form.getFieldsValue();
		const payload = buildFilterPayload(snapshot, filterConfigs) ?? {};

		saveMutate(payload, {
			onSuccess: () => {
				initialValuesRef.current = snapshot;
				form.setFieldsValue(snapshot);
				setCurrentValues(snapshot);
				setDirtyFilters({});
				setIsDirty(false);
			},
		});
	}, [form, filterConfigs, saveMutate]);

	/** Revert draft to saved baseline. */
	const reset = useCallback(() => {
		const saved = initialValuesRef.current;
		form.setFieldsValue(saved);
		setCurrentValues(saved);
		setDirtyFilters({});
		setIsDirty(false);
	}, [form]);

	/** Reset a single filter to its full range (absolute min/max). */
	const clearFilter = useCallback(
		filterId => {
			const config = filterConfigs.find(c => c.id === filterId);
			if (!config) return;
			form.setFieldValue(filterId, { min: config.min, max: config.max });
			handleValuesChange(null, form.getFieldsValue());
		},
		[form, filterConfigs, handleValuesChange]
	);

	// ─── Return ──────────────────────────────────────────────────────────────

	return {
		form,
		initialValues,
		filterConfigs,
		currentValues,
		isDirty,
		activeFilters,
		dirtyFilters,
		activeCount,
		shownCount,
		totalCount,
		isSaving: saveMutation.isPending,
		clearFilter,
		apply,
		reset,
		handleValuesChange,
	};
};

export default useCollectionFilters;
